import {
  AggregateType,
  DataAggregateVO,
  DataFieldValuesRequestVO,
  DataGroupVO,
  DataRequestType,
  DataRequestVO,
  DataResponseVO,
  DataSortVO,
  FieldValuesFilterType,
  FieldValuesFilterVO,
  FilterFieldVO,
} from '@mc/data-filter-react';
import { parseISO } from 'date-fns';
import { FilterDef, isRangeValue } from '../filter/Filters';
import { FilterType } from '../filter/types';
import { getDataRowColumnValue } from '../data-table/dataUtils';
import { FinalTableColumn, GROUP_PREFIX } from './FinalTableProps';
import { getIdOrValueFromRawValue } from './utils';
import { NULL_VALUE } from './useFinalTable';

type Result<DATA, FILTER> = (
  request: DataRequestVO<FILTER>
) => Promise<DataResponseVO<DATA>>;

export type FinalTableApiWrapperConfig<DATA> = {
  columns?: { [key: string]: FinalTableApiWrapperColumnItem<DATA> };
  filters?: { [key: string]: FinalTableApiWrapperFilterItem<DATA> };
};

export type FinalTableApiWrapperColumnItem<DATA> = {
  /**
   * Pokud je definováno, tak se použije jako hodnota pro distinct - tedy např. pro klíč seskupeného řádku apod. Zároveň se použije jako hodnota pro filtr na sloupci (fieldValues), pokud není uvedeno, tak se pro filtr na sloupci použije hodnota dle názvu fieldu.
   */
  distinctValue?: string;
  /**
   * Custom definice řazení.
   * Pokud je string, tak se použije jako cesta k hodnotě, která se má použít pro standardní řazení dle typu hodnoty.
   * Pokud je funkce, tak se použije jako funkce pro řazení.
   */
  sort?: string | ((rowA: DATA, rowB: DATA) => number);
  /**
   * Pokud je definováno, tak se použije jako cesta k hodnotě pro fulltextové vyhledávání - hledání v hodnotách sloupce (např. pro filtr na sloupci).
   */
  fulltextValue?: string;
};

export type FinalTableApiWrapperFilterItem<DATA> = {
  /**
   * Pokud je definováno, tak se použije jako cesta k hodnotě pro filtr, pokud je funkce, tak se použije jako funkce pro filtrování.
   */
  filter?:
    | string
    | ((
        row: DATA,
        emptyValue: boolean,
        notEmptyValue: boolean,
        value?: any | any[]
      ) => boolean);
};

/**
 * Podobně jako lodash.get, ale optimalizovaně, protože split je pomalý.
 * @param path
 * @param defaultValue
 */
function get(path: string, defaultValue?: any) {
  const keys = path.split('.');
  return (obj: any) => {
    let result = obj;

    for (const key of keys) {
      result = result?.[key];
      if (result === undefined) {
        return defaultValue;
      }
    }

    return result;
  };
}

/**
 * Formátuje časový údaj na textový řetězec.
 * @param ms čas v milisekundách
 */
const formatDuration = (ms: number): string => {
  const milliseconds = ms % 1000;
  const seconds = Math.floor((ms / 1000) % 60);
  const minutes = Math.floor((ms / (1000 * 60)) % 60);
  const hours = Math.floor((ms / (1000 * 60 * 60)) % 24);

  const hoursStr = hours > 0 ? `${hours}h ` : '';
  const minutesStr = minutes > 0 ? `${minutes}m ` : '';
  const secondsStr = seconds > 0 ? `${seconds}s ` : '';
  const millisecondsStr = milliseconds > 0 ? `${milliseconds}ms` : '';

  return `${hoursStr}${minutesStr}${secondsStr}${millisecondsStr}`.trim();
};

/**
 * Vrátí unikátní hodnoty pro daný sloupec - tedy projde všechna data a vrátí jen ty, které jsou v daném sloupci unikátní.
 * @param data
 * @param fieldValuesRequest
 * @param config
 */
const getDistinctValues = <DATA>(
  data: DATA[],
  fieldValuesRequest: DataFieldValuesRequestVO,
  config?: FinalTableApiWrapperConfig<DATA>
): DATA[] => {
  let distinctGet: any = undefined;

  if (config?.columns?.[fieldValuesRequest.field]?.distinctValue) {
    // @ts-ignore
    distinctGet = get(config.columns[fieldValuesRequest.field].distinctValue);
  }

  const seen = new Set();
  return data.filter((row) => {
    // @ts-ignore
    let value = distinctGet ? distinctGet(row) : row[fieldValuesRequest.field];
    if (seen.has(value)) {
      return false;
    }
    seen.add(value);
    return true;
  });
};

/**
 * Seřadí řádky
 * @param data data
 * @param sort sort
 * @param config config - pro některé sloupce můžeme definovat vlastní řazení
 * @returns seřazená data
 */
const sortRows = <DATA>(
  data: DATA[],
  sort?: DataSortVO[],
  config?: FinalTableApiWrapperConfig<DATA>
): DATA[] => {
  if (!sort) {
    return data;
  }

  const fieldSortMap: {
    [key: string]: (obj: any) => any;
  } = {};
  for (const s of sort) {
    let fieldSort = config?.columns?.[s.field]?.sort;
    if (typeof fieldSort === 'string') {
      fieldSortMap[s.field] = get(fieldSort);
    }
  }

  return data.sort((a, b) => {
    for (const s of sort) {
      let n;

      // Načtení hodnoty pro řazení
      let aValue;
      let bValue;
      let fieldSort = config?.columns?.[s.field]?.sort;
      if (fieldSort) {
        if (typeof fieldSort === 'string') {
          aValue = fieldSortMap[s.field](a);
          bValue = fieldSortMap[s.field](b);
        } else if (typeof fieldSort === 'function') {
          aValue = a;
          bValue = b;
        } else {
          console.error('Unsupported sort type:', fieldSort);
          throw new Error('Unsupported sort type: ' + fieldSort);
        }
      } else {
        // @ts-ignore
        aValue = a[s.field];
        // @ts-ignore
        bValue = b[s.field];
      }

      // Řazení
      if (aValue === bValue) {
        n = 0;
      } else if (
        (aValue === null || aValue === undefined) &&
        (bValue === null || bValue === undefined)
      ) {
        n = 0;
      } else if (aValue === null || aValue === undefined) {
        n = -1;
      } else if (bValue === null || bValue === undefined) {
        n = 1;
      } else if (typeof fieldSort === 'function') {
        n = fieldSort(a, b);
      } else if (typeof aValue === 'string') {
        n = aValue.localeCompare(bValue);
      } else if (typeof aValue === 'number') {
        n = aValue - bValue;
      } else if (typeof aValue === 'boolean') {
        n = aValue ? -1 : 1;
      } else {
        console.error(
          'Unsupported sort type:',
          typeof aValue,
          typeof bValue,
          aValue,
          bValue
        );
        throw new Error(
          'Unsupported sort type: ' + typeof aValue + '/' + typeof bValue
        );
      }

      if (n !== 0) {
        return s.asc ? n : -n;
      }
    }
    return 0;
  });
};

/**
 * Vrátí část dat podle requestu - page, range, ids apod.
 * @param data data
 * @param request request
 * @returns část dat dle requestu
 */
const getPartRows = <DATA, FILTER>(
  data: DATA[],
  request: DataRequestVO<FILTER>
): { rows: DATA[]; withoutCount: boolean } => {
  if (request.idsRequest) {
    return {
      // @ts-ignore
      rows: data.filter((row) => request.idsRequest?.ids.includes(row.id)),
      withoutCount: true,
    };
  } else if (request.pageRequest) {
    return {
      rows: data.slice(
        (request.pageRequest.pageNumber - 1) * request.pageRequest.pageSize,
        (request.pageRequest.pageNumber - 1) * request.pageRequest.pageSize +
          request.pageRequest.pageSize
      ),
      withoutCount: request.pageRequest.withoutCount!!,
    };
  } else if (request.rangeRequest) {
    return {
      rows: data.slice(
        request.rangeRequest.firstResult,
        request.rangeRequest.firstResult + request.rangeRequest.maxResults
      ),
      withoutCount: request.rangeRequest.withoutCount || false,
    };
  } else {
    console.error('Unsupported request type:', request.requestType);
    throw new Error('Unsupported request type:' + request.requestType);
  }
};

/**
 * Ponechá jen "sloupce", které jsou v poli fields.
 * @param data data
 * @param fields pole názvů sloupců, které chceme ponechat
 */
const keepColumns = <DATA>(data: DATA[], fields: string[]): DATA[] => {
  return data.map((row) => {
    // @ts-ignore
    const result: DATA = {};

    for (const field of fields) {
      // @ts-ignore
      result[field] = row[field];
    }

    return result;
  });
};

/**
 * Vrátí mapu název filter fieldu na jeho datový typ.
 * @param filterDefs
 */
const getFilterValuesTypes = (
  filterDefs: FilterDef[]
): { [key: string]: ValueType } => {
  const result: { [key: string]: ValueType } = {};

  for (const filterDef of filterDefs) {
    switch (filterDef.type) {
      case 'text':
        result[filterDef.name] = 'string';
        break;
      case 'integer':
      case 'integerRange':
      case 'decimal':
      case 'decimalRange':
      case 'currency':
      case 'currencyRange':
      case 'currencyDecimal':
      case 'currencyDecimalRange':
        result[filterDef.name] = 'number';
        break;
      case 'date':
      case 'dateRange':
        result[filterDef.name] = 'date';
        break;
      case 'dateTime':
      case 'dateTimeRange':
        result[filterDef.name] = 'dateTime';
        break;
      case 'checkbox':
      // result[filterDef.name] = 'boolean';
      // break;
      case 'apiMultiSelect':
      case 'multiSelect':
      case 'selectBox':
      case 'autocomplete':
        result[filterDef.name] = 'realValue';
        break;
      case 'custom':
      default:
        throw new Error('Unsupported filter type: ' + filterDef.type);
    }
  }

  return result;
};

// pokud bude pomalé, optimalizovat - připravit si jen getValue metody (get(columnDef.distinctValue) a (row) => (row[dataGroup.field])) a ty pak už používat pro získání hodnoty z row
/**
 * Vrátí id seskupeného řádku.
 * @param row
 * @param grouping
 * @param config
 */
const getGroupId = <DATA>(
  row: DATA,
  grouping: DataGroupVO[],
  config?: FinalTableApiWrapperConfig<DATA>
): string => {
  const parts: any[] = [];
  for (const dataGroup of grouping) {
    const columnDef = config?.columns?.[dataGroup.field];
    let value;
    if (columnDef?.distinctValue) {
      // @ts-ignore
      value = get(columnDef.distinctValue)(row);
    } else {
      // @ts-ignore
      value = getIdOrValueFromRawValue(row[dataGroup.field]);
    }
    if (value === null || value === undefined) {
      parts.push(NULL_VALUE);
    } else {
      parts.push(value);
    }
  }
  const groupId = parts.join(GROUP_PREFIX);
  return groupId;
};

type GroupRow<DATA> = {
  grpId: string;
  rows: DATA[];
};

/**
 * Seskupí řádky podle seskupení.
 * @param data
 * @param grouping
 * @param config
 */
const groupRows = <DATA>(
  data: DATA[],
  grouping: DataGroupVO[],
  config?: FinalTableApiWrapperConfig<DATA>
): GroupRow<DATA>[] => {
  const result: GroupRow<DATA>[] = [];
  const indexMap: { [key: string]: number } = {};

  for (const row of data) {
    const grpId = getGroupId(row, grouping, config);
    if (indexMap[grpId] === undefined) {
      indexMap[grpId] = result.length;
      result.push({ grpId, rows: [row] });
    } else {
      result[indexMap[grpId]].rows.push(row);
    }
  }

  return result;
};

/**
 * Přefiltruje data podle field values filtru (filtr na slopci).
 * @param data
 * @param filter
 * @param columns
 */
const filterByFieldValuesFilter = <DATA>(
  data: DATA[],
  filter: {
    [key: string]: FieldValuesFilterVO;
  },
  columns: FinalTableColumn<any>[]
): DATA[] => {
  const rows = data.filter((row) => {
    for (const fieldFilterKey of Object.keys(filter)) {
      const fieldFilter = filter[fieldFilterKey];
      const columnDef = columns.find(
        (c) => (c.id || c.accessor) === fieldFilterKey
      );
      if (!columnDef) {
        console.error('Column not found:', fieldFilterKey, columns, filter);
        throw new Error('Column not found:' + fieldFilterKey);
      }

      const columnRawValue = getDataRowColumnValue(
        row,
        columnDef.accessor || columnDef.id
      );
      const columnFilterValue = getIdOrValueFromRawValue(columnRawValue);

      switch (fieldFilter.filterType) {
        case FieldValuesFilterType.VALUES:
          // Porovnání zda fieldFilter.values obsahuje columnValue
          if (
            fieldFilter.values &&
            !fieldFilter.values.includes(columnFilterValue)
          ) {
            return false;
          }
          break;
        default:
          throw new Error('Unsupported filter type: ' + fieldFilter.filterType);
      }
    }

    return true;
  });

  return rows;
};

/**
 * Ponechá jen data, která jsou pod daným seskupeným řádkem (groupFilter).
 * @param data
 * @param groupFilter
 * @param config
 */
const filterByGroupFilter = <DATA>(
  data: DATA[],
  groupFilter: {
    [key: string]: object;
  },
  config?: FinalTableApiWrapperConfig<DATA>
): DATA[] => {
  const rows = data.filter((row) => {
    let groupFilterMatch = true;
    for (const fieldKey of Object.keys(groupFilter)) {
      // pokud bude pomalé, optimalizovat - připravit si jen getValue metody (get(columnDef.distinctValue) a (row) => (row[dataGroup.field])) a ty pak už používat pro získání hodnoty z row
      const columnDef = config?.columns?.[fieldKey];
      let value;
      if (columnDef?.distinctValue) {
        // @ts-ignore
        value = get(columnDef.distinctValue)(row);
      } else {
        // @ts-ignore
        value = getIdOrValueFromRawValue(row[fieldKey]);
      }

      if (
        typeof value === 'boolean' ||
        typeof value === 'number' ||
        typeof value === 'bigint'
      ) {
        value = `${value}`;
      }

      const filterValue = groupFilter[fieldKey];

      if (filterValue === null || filterValue === undefined) {
        if (value !== null && value !== undefined) {
          groupFilterMatch = false;
          break;
        }
      } else if (filterValue != value) {
        groupFilterMatch = false;
        break;
      }
    }

    return groupFilterMatch;
  });

  return rows;
};

/**
 * Aplikuje filtry na data.
 * @param data
 * @param filter
 * @param filterDefs
 * @param filterType
 * @param config
 */
const filterByFilter = <DATA, FILTER>(
  data: DATA[],
  filter: FILTER,
  filterDefs: FilterDef[],
  filterType: FilterType,
  config?: FinalTableApiWrapperConfig<DATA>
): DATA[] => {
  const fieldValueTypes = getFilterValuesTypes(filterDefs);

  let rows = data;
  for (const filterDef of filterDefs) {
    let emptyValue = false;
    let notEmptyValue = false;
    let filterValue: any = null;

    const isRange = isRangeValue(filterDef.type);

    if (filterType === 'default') {
      // @ts-ignore
      const ff = filter[filterDef.name] as FilterFieldVO<any>;
      if (!ff) {
        continue;
      }

      emptyValue = !!ff.emptyValue;
      notEmptyValue = !!ff.notEmptyValue;
      filterValue = ff.value;
    } else {
      // @ts-ignore
      const ff = filter[filterDef.name] as any;
      if (ff === null || ff === undefined) {
        continue;
      }

      filterValue = ff;
    }

    // -----------

    const emptyFilterValue = isRange
      ? (filterValue[0] === null || filterValue[0] === undefined) &&
        (filterValue[1] === null || filterValue[1] === undefined)
      : filterValue === null ||
        filterValue === undefined ||
        (Array.isArray(filterValue) && filterValue.length === 0);
    if (emptyFilterValue && !emptyValue && !notEmptyValue) {
      continue;
    }

    // -----------

    let getValue: ((obj: any) => any) | undefined = undefined;
    let filterFc:
      | ((
          row: DATA,
          emptyValue: boolean,
          notEmptyValue: boolean,
          value?: any | any[]
        ) => boolean)
      | undefined = undefined;
    if (typeof config?.filters?.[filterDef.name]?.filter === 'function') {
      // @ts-ignore
      filterFc = config?.filters?.[filterDef.name]?.filter;
    } else {
      getValue = get(
        // @ts-ignore
        config?.filters?.[filterDef.name]?.filter ?? filterDef.name
      );
    }

    if (isRange) {
      rows = findByRange(
        filterDef.name,
        filterValue[0],
        filterValue[1],
        fieldValueTypes,
        rows,
        emptyValue,
        notEmptyValue,
        getValue,
        filterFc
      );
    } else {
      if (filterFc) {
        rows = rows.filter((row) => {
          // @ts-ignore
          return filterFc(row, emptyValue, notEmptyValue, filterValue);
        });
      } else if (getValue) {
        if (emptyValue) {
          rows = rows.filter((row) => {
            // @ts-ignore
            return getValue(row) === null || getValue(row) === undefined;
          });
        } else if (notEmptyValue) {
          rows = rows.filter((row) => {
            // @ts-ignore
            return getValue(row) !== null && getValue(row) !== undefined;
          });
        } else {
          if (fieldValueTypes[filterDef.name] === 'string') {
            rows = findByFulltext(filterValue, rows, getValue);
          } else {
            rows = findByEqual(filterValue, rows, getValue);
          }
        }
      } else {
        console.error(
          'Unsupported filter type:',
          fieldValueTypes[filterDef.name]
        );
        throw new Error(
          'Unsupported filter type:' + fieldValueTypes[filterDef.name]
        );
      }
    }
  }

  return rows;
};

/**
 * Hledá řádky na equel na předané hodnoty.
 * @param filterValue
 * @param data
 * @param getValue
 */
function findByEqual<DATA>(
  filterValue: any | any[],
  data: DATA[],
  getValue: (obj: any) => any
) {
  return data.filter((row) => {
    // @ts-ignore
    let value: any = getValue(row);

    if (Array.isArray(filterValue)) {
      return filterValue.includes(value);
    } else {
      return value === filterValue;
    }
  });
}

/**
 * Hledá řádky dle předaného fulltextu - JEN contains s ignorováním diakritiky a velikosti písmen
 * @param fulltextString
 * @param data
 * @param getValue
 */
function findByFulltext<DATA>(
  fulltextString: string,
  data: DATA[],
  getValue: (obj: any) => any
) {
  // @ts-ignore
  const fulltext = fulltextString
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

  // Fulltext by měl být jen na textové sloupce
  return data.filter((row) => {
    // @ts-ignore
    let value: any = getValue(row);
    if (value && typeof value === 'string') {
      value = value
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '');
    } else {
      value = '';
    }

    return value.indexOf(fulltext) !== -1;
  });
}

/**
 * Hledá řádky dle range hodnot.
 * @param field
 * @param from
 * @param to
 * @param fieldValueTypes
 * @param data
 * @param emptyValue
 * @param notEmptyValue
 * @param getValue
 * @param filterFc
 */
function findByRange<DATA>(
  field: string,
  from: any,
  to: any,
  fieldValueTypes: {
    [key: string]: ValueType;
  },
  data: DATA[],
  emptyValue: boolean,
  notEmptyValue: boolean,
  getValue?: (obj: DATA) => any,
  filterFc?: (
    row: DATA,
    emptyValue: boolean,
    notEmptyValue: boolean,
    value?: any | any[]
  ) => boolean
) {
  if (!getValue && !filterFc) {
    throw new Error('getValue or filterFc is required');
  }

  // @ts-ignore
  const inputValueFrom = from;
  // @ts-ignore
  const inputValueTo = to;

  if (fieldValueTypes[field] === 'date') {
    const valueFrom = inputValueFrom ? parseISO(inputValueFrom) : null;
    const valueTo = inputValueTo ? parseISO(inputValueTo) : null;

    // @ts-ignore
    return data.filter((row) => {
      // @ts-ignore
      let val = getValue(row);
      if (emptyValue && (val === null || val === undefined)) {
        return true;
      } else if (notEmptyValue && val !== null && val !== undefined) {
        return true;
      } else if (val === null || val === undefined) {
        return false;
      }

      if (filterFc) {
        // Zde schválně chceme inputValuesFrom a inputValueTo
        return filterFc(row, emptyValue, notEmptyValue, [
          inputValueFrom,
          inputValueTo,
        ]);
      } else {
        let value = parseISO(val);

        // From
        if (
          valueFrom !== null &&
          valueFrom !== undefined &&
          +value < +valueFrom
        ) {
          return false;
        }

        // To
        if (valueTo !== null && valueTo !== undefined && +value > +valueTo) {
          return false;
        }

        return true;
      }
    });
  } else if (fieldValueTypes[field] === 'dateTime') {
    const valueFrom = inputValueFrom ? parseISO(inputValueFrom) : null;
    const valueTo = inputValueTo ? parseISO(inputValueTo) : null;
    if (valueFrom) {
      valueFrom.setSeconds(0, 0);
    }
    if (valueTo) {
      valueTo.setSeconds(0, 0);
    }

    // @ts-ignore
    return data.filter((row) => {
      if (filterFc) {
        // Zde schválně chceme inputValuesFrom a inputValueTo
        return filterFc(row, emptyValue, notEmptyValue, [
          inputValueFrom,
          inputValueTo,
        ]);
      } else {
        // @ts-ignore
        let val = getValue(row);
        if (val === null || val === undefined) {
          return false;
        }

        let value = parseISO(val);
        value.setSeconds(0, 0);

        // From
        if (
          valueFrom !== null &&
          valueFrom !== undefined &&
          +value < +valueFrom
        ) {
          return false;
        }

        // To
        if (valueTo !== null && valueTo !== undefined && +value > +valueTo) {
          return false;
        }

        return true;
      }
    });
  } else if (fieldValueTypes[field] === 'number') {
    const valueFrom = inputValueFrom;
    const valueTo = inputValueTo;

    // @ts-ignore
    return data.filter((row) => {
      if (filterFc) {
        // Zde schválně chceme inputValuesFrom a inputValueTo
        return filterFc(row, emptyValue, notEmptyValue, [
          inputValueFrom,
          inputValueTo,
        ]);
      } else {
        // @ts-ignore
        let value = getValue(row);

        if (value === null || value === undefined) {
          return false;
        }

        // From
        if (
          valueFrom !== null &&
          valueFrom !== undefined &&
          value < valueFrom
        ) {
          return false;
        }

        // To
        if (valueTo !== null && valueTo !== undefined && value > valueTo) {
          return false;
        }

        return true;
      }
    });
  } else {
    console.error('Unsupported column type:', fieldValueTypes[field]);
    throw new Error('Unsupported column type:' + fieldValueTypes[field]);
  }
}

/**
 * Filtrování v hodnotách sloupce - pro vracení hodnot sloupce na klienta.
 * @param data
 * @param fieldValuesRequest
 * @param config
 */
const filterByFieldValuesRequest = <DATA>(
  data: DATA[],
  fieldValuesRequest: DataFieldValuesRequestVO,
  config?: FinalTableApiWrapperConfig<DATA>
): DATA[] => {
  const fieldValueTypes = getFieldValuesTypes(
    data,
    [fieldValuesRequest.field],
    config
  );

  const byFulltext: boolean = !!fieldValuesRequest.fulltext;
  const byRange =
    fieldValuesRequest.range?.length === 2 &&
    ((fieldValuesRequest.range[0] !== null &&
      fieldValuesRequest.range[0] !== undefined) ||
      (fieldValuesRequest.range[1] !== null &&
        fieldValuesRequest.range[1] !== undefined));

  if (!byFulltext && !byRange) {
    return data;
  }

  if (!fieldValuesRequest.field) {
    console.error('fieldValuesRequest.field is required', fieldValuesRequest);
    throw new Error('fieldValuesRequest.field is required');
  }

  const getValue = get(
    config?.columns?.[fieldValuesRequest.field]?.fulltextValue ??
      fieldValuesRequest.field
  );

  if (byFulltext) {
    return findByFulltext(
      // @ts-ignore
      fieldValuesRequest.fulltext,
      data,
      getValue
    );
  } else if (byRange) {
    return findByRange(
      fieldValuesRequest.field,
      // @ts-ignore
      fieldValuesRequest.range[0],
      // @ts-ignore
      fieldValuesRequest.range[1],
      fieldValueTypes,
      data,
      false,
      false,
      getValue,
      undefined
    );
  } else {
    console.error('Unsupported fieldValuesRequest:', fieldValuesRequest);
    throw new Error('Unsupported fieldValuesRequest');
  }
};

type ValueType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'date'
  | 'dateTime'
  | 'realValue';

/**
 * Vrátí mapu název sloupce na jeho datový typ - "odhaduje" z dat.
 * @param data
 * @param fields
 * @param config
 */
const getFieldValuesTypes = <DATA>(
  data: DATA[],
  fields: string[],
  config?: FinalTableApiWrapperConfig<DATA>
): { [key: string]: ValueType } => {
  const result: { [key: string]: ValueType } = {};

  for (const column of fields) {
    let allDateWithZeroTime = true;
    let allDate = true;
    let allDateTime = true;

    for (const row of data) {
      // @ts-ignore
      const val = row[column];
      if (val === null || val === undefined) {
        continue;
      }

      // ---

      if (typeof val === 'number' || typeof val === 'bigint') {
        result[column] = 'number';
        break;
      } else if (typeof val === 'boolean') {
        result[column] = 'boolean';
        break;
      } else if (typeof val === 'object') {
        result[column] = 'string';
        const fulltextPath = config?.columns?.[column]?.fulltextValue;
        if (!fulltextPath) {
          console.warn(
            'fulltextValue is not defined for object column:',
            column
          );
        }
        break;
      } else {
        const isDate = val.match(/^\d{4}-\d{2}-\d{2}$/) || false;
        const isDateTime =
          val.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/) || false;
        const isDateWithZeroTime =
          val.match(/^\d{4}-\d{2}-\d{2}T00:00:00/) || false;
        if (!isDate) {
          allDate = false;
        }
        if (!isDateTime) {
          allDateTime = false;
        }
        if (!isDateWithZeroTime) {
          allDateWithZeroTime = false;
        }
      }
    }

    if (!result[column]) {
      if (allDateWithZeroTime) {
        result[column] = 'date';
      } else if (allDateTime) {
        result[column] = 'dateTime';
      } else if (allDate) {
        result[column] = 'date';
      } else {
        result[column] = 'string';
      }
    }
  }

  return result;
};

type AggrFc = (accValue: any, newValue: any) => any;

/**
 * Vrátí mapu field -> AggrFc - tedy funkci pro výpočet hodnoty agregační funkce.
 * @param aggregate
 * @param columns
 */
const getAggregateFunctionMap = (
  aggregate: DataAggregateVO[],
  columns: FinalTableColumn<any>[]
): { [key: string]: AggrFc } => {
  const aggregateFunctionMap: { [key: string]: AggrFc } = {};

  aggregate.forEach((agg) => {
    const columnDef = columns.find((c) => (c.id || c.accessor) === agg.field);
    if (!columnDef) {
      console.error('Column not found:', agg.field, columns, aggregate);
      throw new Error('Column not found:' + agg.field);
    }

    let fc: AggrFc | undefined;

    switch (agg.type) {
      case AggregateType.AVG:
        const avgFc = () => {
          let sum: number | undefined = undefined;
          let count: number | undefined = undefined;

          // @ts-ignore
          return (_, newValue) => {
            if (newValue === undefined || newValue === null) {
              if (sum === undefined || count === undefined) {
                return undefined;
              } else {
                return sum / count;
              }
            }

            if (sum === undefined || count === undefined) {
              sum = newValue;
              count = 1;

              return newValue;
            } else {
              sum += newValue;
              count++;
              return sum / count;
            }
          };
        };

        fc = avgFc();

        break;
      case AggregateType.COUNT:
        fc = (accValue) => {
          if (accValue === undefined) {
            return 1;
          } else {
            return accValue + 1;
          }
        };
        break;
      case AggregateType.SUM:
        fc = (accValue, newValue) => {
          if (newValue === undefined || newValue === null) {
            return accValue;
          } else if (accValue === undefined) {
            return newValue;
          } else {
            return accValue + newValue;
          }
        };
        break;
      case AggregateType.MIN:
        fc = (accValue, newValue) => {
          if (newValue === undefined || newValue === null) {
            return accValue;
          } else if (accValue === undefined) {
            return newValue;
          } else {
            if (columnDef.type === 'date' || columnDef.type === 'dateTime') {
              if (newValue < accValue) {
                return newValue;
              } else {
                return accValue;
              }
            } else {
              return Math.min(accValue, newValue);
            }
          }
        };
        break;
      case AggregateType.MAX:
        fc = (accValue, newValue) => {
          if (newValue === undefined || newValue === null) {
            return accValue;
          } else if (accValue === undefined) {
            return newValue;
          } else {
            if (columnDef.type === 'date' || columnDef.type === 'dateTime') {
              if (newValue > accValue) {
                return newValue;
              } else {
                return accValue;
              }
            } else {
              return Math.max(accValue, newValue);
            }
          }
        };
        break;
      default:
        throw new Error('Unsupported aggregate type: ' + agg.type);
    }
    if (fc) {
      aggregateFunctionMap[agg.field] = fc;
    }
  });

  return aggregateFunctionMap;
};

/**
 * Naplní řádek o agregované hodnoty.
 * @param row kam se bude plnit
 * @param rows z jakých řádků se počítá agregace
 * @param aggregateFunctionMap
 * @param aggregate
 */
const fillAggravatedRow = <DATA>(
  row: DATA,
  rows: DATA[],
  aggregateFunctionMap: { [key: string]: AggrFc },
  aggregate: DataAggregateVO[]
) => {
  rows.forEach((gr) => {
    aggregate.forEach((agg) => {
      // @ts-ignore
      row.aggregate[agg.field] = aggregateFunctionMap[agg.field](
        // @ts-ignore
        row.aggregate[agg.field],
        // @ts-ignore
        gr[agg.field]
      );
    });
  });
};

/**
 * Načtení sumařizovaného řádku.
 * @param rows
 * @param columns
 * @param aggregate
 */
const getSummarizeRow = <DATA>(
  rows: DATA[],
  columns: FinalTableColumn<any>[],
  aggregate?: DataAggregateVO[]
): DATA => {
  // @ts-ignore
  const row: DATA = { aggregate: {} };

  const hasAggregate = aggregate && aggregate.length > 0;
  if (hasAggregate) {
    let aggregateFunctionMap = getAggregateFunctionMap(aggregate, columns);
    fillAggravatedRow(row, rows, aggregateFunctionMap, aggregate);
  }

  // @ts-ignore
  return row;
};

/**
 * Předané seskupené řádky transformuje na řádků pro vácení, do kterých přidává i agregované hodnoty.
 * @param groupedRows
 * @param grouping
 * @param columns
 * @param aggregate
 */
const getFlatGroupedRows = <DATA>(
  groupedRows: GroupRow<DATA>[],
  grouping: DataGroupVO[],
  columns: FinalTableColumn<any>[],
  aggregate?: DataAggregateVO[]
): DATA[] => {
  const hasAggregate = aggregate && aggregate.length > 0;
  let aggregateFunctionMap: { [key: string]: AggrFc };

  return groupedRows.map((groupedRow) => {
    const row: any = {};

    // GrpId a grpCount
    row.aggregate = {
      grpId: groupedRow.grpId,
      grpCount: groupedRow.rows.length,
    };

    // Datové fieldy + agregované fieldy
    for (const dataGroup of grouping) {
      // Datový field seskupených řádků
      // @ts-ignore
      row[dataGroup.field] = groupedRow.rows[0][dataGroup.field];

      // Agregované fieldy
      if (hasAggregate) {
        // Kvůli AVG kešování SUM a COUNT musí pro každý group řádek mít REINIT
        aggregateFunctionMap = getAggregateFunctionMap(aggregate, columns);

        fillAggravatedRow(
          row,
          groupedRow.rows,
          aggregateFunctionMap,
          aggregate
        );
      }
    }

    return row;
  });
};

let apiCallCounter = 0;

/**
 * Vrapper pro final table API pro lokální data.
 * @param data lokální data
 * @param columns sloupce
 * @param filterDef definice filtrů
 * @param config konfigurace
 * @param filterType typ filtru
 */
export const finalTableApiWrapper = <DATA, FILTER>(
  data: DATA[],
  columns: FinalTableColumn<any>[],
  filterDef: FilterDef[],
  config?: FinalTableApiWrapperConfig<DATA>,
  filterType?: FilterType
): Result<DATA, FILTER> => {
  return (request: DataRequestVO<FILTER>): Promise<DataResponseVO<DATA>> => {
    try {
      const startTime = new Date().getTime();
      if (process.env.NODE_ENV !== 'production') {
        apiCallCounter++;
        console.log('🪻 >>', `#${apiCallCounter}`, request);
      }

      let rows = data;

      // Filtrování
      // - dle filter
      if (request.filter) {
        rows = filterByFilter(
          rows,
          request.filter,
          filterDef,
          filterType ?? 'default',
          config
        );
      }

      // Filtrování
      // - dle fieldValuesFilter
      if (request.fieldValuesFilter) {
        rows = filterByFieldValuesFilter(
          rows,
          request.fieldValuesFilter,
          columns
        );
      }

      // Filtrování
      // - dle groupFilter
      if (request.groupFilter) {
        rows = filterByGroupFilter(rows, request.groupFilter, config);
      }

      // Sort
      if (request.sort && request.sort.length > 0) {
        // Pokud se zdrojová data zatím neodkopírovala filtrováním, opdokopírujeme zde, abychom nezasahovali do původních dat
        if (rows === data) {
          rows = [...data];
        }
        rows = sortRows(rows, request.sort, config);
      }

      // ---------------------------------------------------------

      let result: DataResponseVO<DATA>;

      switch (request.requestType) {
        case DataRequestType.DATA:
          {
            const partRows = getPartRows(rows, request);
            result = {
              rows: partRows.rows,
              totalDataElements: partRows.withoutCount ? 0 : rows.length,
              totalElements: partRows.withoutCount ? 0 : rows.length,
            };
          }
          break;
        case DataRequestType.FIELD_VALUES:
          {
            if (!request.fieldValuesRequest) {
              throw new Error('fieldValuesRequest is required');
            } else {
              // Distinct
              rows = getDistinctValues(
                rows,
                request.fieldValuesRequest,
                config
              );

              // Filtrování - dle fieldValuesRequest
              rows = filterByFieldValuesRequest(
                rows,
                request.fieldValuesRequest,
                config
              );

              // Stránkování
              const partRows = getPartRows(rows, request);

              // Ponechání jen sloupce který chceme vracet
              rows = keepColumns(partRows.rows, [
                request.fieldValuesRequest.field,
              ]);

              // Result
              result = {
                rows: rows,
                totalDataElements: 0,
                totalElements: 0,
              };
            }
          }
          break;
        case DataRequestType.GROUP_DATA:
          if (!request.grouping || request.grouping.length === 0) {
            throw new Error('grouping is required');
          }
          const groupdRows = groupRows(rows, request.grouping, config);
          result = {
            rows: getFlatGroupedRows(
              groupdRows,
              request.grouping,
              columns,
              request.aggregate
            ),
            // @ts-ignore
            totalDataElements: null,
            // @ts-ignore
            totalElements: null,
          };
          break;
        case DataRequestType.IDS:
          if (request.grouping && request.grouping.length > 0) {
            const groupdRows = groupRows(rows, request.grouping, config);

            // @ts-ignore
            let ids = groupdRows.map((groupedRow) => groupedRow.grpId);
            result = {
              ids: ids,
              // @ts-ignore
              totalDataElements:
                request.grouping.length === 1 ? rows.length : null,
              totalElements: ids.length,
            };
          } else {
            // @ts-ignore
            let ids = rows.map((row) => row.id);
            result = {
              ids: ids,
              totalDataElements: ids.length,
              totalElements: ids.length,
            };
          }
          break;
        case DataRequestType.SUMMARIZE:
          const summarizeRow = getSummarizeRow(
            rows,
            columns,
            request.aggregate
          );
          result = {
            rows: [summarizeRow],
            // @ts-ignore
            totalDataElements: null,
            // @ts-ignore
            totalElements: null,
          };
          break;
        default:
          throw new Error('Request type not supported: ' + request.requestType);
      }

      const endTime = new Date().getTime();
      if (process.env.NODE_ENV !== 'production') {
        console.log(
          '🪻 <<',
          `#${apiCallCounter}`,
          formatDuration(endTime - startTime),
          request,
          result
        );
      }

      return Promise.resolve(result);
    } catch (e) {
      console.error('Error in finalTableApiWrapper', e);
      return Promise.reject(e);
    }
  };
};
